import { useState } from 'react';
import { cn, getInitials, stringToColor } from '@/lib/utils';

interface AvatarProps {
  src?: string;
  name: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizes = {
  xs: 'w-6 h-6 text-[10px]',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
};

export function Avatar({ src, name, size = 'md', className }: AvatarProps) {
  const [imgError, setImgError] = useState(false);
  const initials = getInitials(name);
  const bgColor = stringToColor(name);

  if (src && !imgError) {
    return (
      <img
        src={src}
        alt={name}
        className={cn('avatar', sizes[size], className)}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div
      className={cn('avatar', sizes[size], className)}
      style={{ backgroundColor: bgColor }}
    >
      <span className="text-white font-semibold">{initials}</span>
    </div>
  );
}

interface AvatarGroupProps {
  users: Array<{ name: string; avatarUrl?: string }>;
  max?: number;
  size?: 'xs' | 'sm' | 'md';
}

export function AvatarGroup({ users, max = 4, size = 'sm' }: AvatarGroupProps) {
  const visible = users.slice(0, max);
  const remaining = users.length - max;

  return (
    <div className="flex -space-x-2">
      {visible.map((user, i) => (
        <Avatar
          key={i}
          src={user.avatarUrl}
          name={user.name}
          size={size}
          className="ring-2 ring-white"
        />
      ))}
      {remaining > 0 && (
        <div className={cn(
          'avatar ring-2 ring-white bg-grey-200 text-grey-600',
          sizes[size]
        )}>
          +{remaining}
        </div>
      )}
    </div>
  );
}
